import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { 
  Search, UserCheck, Handshake, CheckCircle, 
  ClipboardList, MessageSquare, FileCheck, Rocket
} from 'lucide-react';

const CommentCaMarchePage = () => {
  const { t } = useTranslation();

  const stepsEntreprises = [
    {
      icon: Search,
      title: t('howItWorksPage.forBusinesses.step1.title'),
      description: t('howItWorksPage.forBusinesses.step1.description')
    },
    {
      icon: UserCheck,
      title: t('howItWorksPage.forBusinesses.step2.title'),
      description: t('howItWorksPage.forBusinesses.step2.description')
    },
    {
      icon: Handshake,
      title: t('howItWorksPage.forBusinesses.step3.title'),
      description: t('howItWorksPage.forBusinesses.step3.description')
    },
    {
      icon: CheckCircle,
      title: t('howItWorksPage.forBusinesses.step4.title'),
      description: t('howItWorksPage.forBusinesses.step4.description')
    }
  ];

  const stepsRetraites = [
    {
      icon: ClipboardList,
      title: t('howItWorksPage.forRetirees.step1.title'),
      description: t('howItWorksPage.forRetirees.step1.description')
    },
    {
      icon: MessageSquare,
      title: t('howItWorksPage.forRetirees.step2.title'),
      description: t('howItWorksPage.forRetirees.step2.description')
    },
    {
      icon: FileCheck,
      title: t('howItWorksPage.forRetirees.step3.title'),
      description: t('howItWorksPage.forRetirees.step3.description')
    },
    {
      icon: Rocket,
      title: t('howItWorksPage.forRetirees.step4.title'),
      description: t('howItWorksPage.forRetirees.step4.description')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 section-cream">
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-6">
                {t('howItWorksPage.title')}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t('howItWorksPage.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* For Businesses */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
                {t('howItWorksPage.forBusinesses.badge')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
                {t('howItWorksPage.forBusinesses.title')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stepsEntreprises.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className="card-elevated p-6 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif font-bold">
                          {index + 1}
                        </div>
                        <div className="icon-circle">
                          <IconComponent size={20} />
                        </div>
                      </div>
                      <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* For Retirees */}
        <section className="py-16 lg:py-20 section-cream">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
                {t('howItWorksPage.forRetirees.badge')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
                {t('howItWorksPage.forRetirees.title')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stepsRetraites.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="bg-card rounded-xl p-6 border border-border/50 h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif font-bold">
                          {index + 1}
                        </div>
                        <div className="icon-circle">
                          <IconComponent size={20} />
                        </div>
                      </div>
                      <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
                {t('howItWorksPage.platform.title')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔒</span>
                </div>
                <h3 className="font-serif font-semibold text-foreground mb-2">{t('howItWorksPage.platform.secure.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('howItWorksPage.platform.secure.description')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="font-serif font-semibold text-foreground mb-2">{t('howItWorksPage.platform.flexible.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('howItWorksPage.platform.flexible.description')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🤝</span>
                </div>
                <h3 className="font-serif font-semibold text-foreground mb-2">{t('howItWorksPage.platform.transparent.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('howItWorksPage.platform.transparent.description')}
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CommentCaMarchePage;
